<template>
  <ConfirmDialog></ConfirmDialog>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-4">Organizations Management</h1>
    <div class="card">
      <DataTable :value="store.organizations" :loading="store.loading" tableStyle="min-width: 50rem">
        <template #header>
          <div class="flex justify-end">
            <Button label="Add Organization" icon="pi pi-plus" @click="openCreateDialog" />
          </div>
        </template>
        <Column field="org_id" header="ID" sortable></Column>
        <Column field="org_name" header="Name" sortable></Column>
        <Column field="user_count" header="Users" sortable></Column>
        <Column field="domain_count" header="Domains" sortable></Column>
        <Column field="status" header="Status" sortable></Column>
        <Column field="created_at" header="Created At" sortable>
          <template #body="slotProps">
            {{ new Date(slotProps.data.created_at).toLocaleString() }}
          </template>
        </Column>
        <Column header="Actions" style="width: 8rem">
          <template #body="slotProps">
            <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="openEditDialog(slotProps.data)" />
            <Button icon="pi pi-trash" class="p-button-rounded p-button-warning" @click="deleteOrg(slotProps.data.org_id)" />
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Add Dialog -->
    <Dialog v-model:visible="addDialogVisible" header="Add Organization" :modal="true" class="p-fluid w-full max-w-md">
      <div class="field">
        <label for="org_name">Name</label>
        <InputText id="org_name" v-model.trim="newOrg.org_name" required="true" autofocus />
      </div>
      <div class="field">
        <label for="status">Status</label>
        <Dropdown id="status" v-model="newOrg.status" :options="statusOptions" optionLabel="label" optionValue="value" placeholder="Select a Status" />
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="addDialogVisible = false"/>
        <Button label="Save" icon="pi pi-check" @click="saveNewOrganization" />
      </template>
    </Dialog>

    <!-- Edit Dialog -->
    <Dialog v-if="currentOrg" v-model:visible="editDialogVisible" :header="`Edit: ${currentOrg.org_name}`" :modal="true" 
            :style="{ width: '70vw', height: '90vh' }" :maximizable="true"
            :breakpoints="{'960px': '95vw', '640px': '100vw'}" contentClass="p-0 flex flex-col">
      
      <!-- Mobile Menu (Dropdown) -->
      <div class="md:hidden p-4 border-b surface-border" style="background-color: var(--p-surface-card)">
        <Dropdown v-model="activeEditTab" :options="editMenu.map(m => m.label)" class="w-full" />
      </div>

      <div class="flex flex-grow overflow-hidden">
        <!-- Left Menu (Desktop) -->
        <div class="hidden md:block w-[250px] p-4 border-r-2 surface-border" style="background-color: var(--p-surface-card)">
          <ul class="space-y-2">
            <li v-for="item in editMenu" :key="item.label" @click="activeEditTab = item.label" 
                class="p-3 rounded-md cursor-pointer flex items-center text-color hover:bg-surface-hover transition-colors" 
                :class="{ 'bg-primary-500 text-primary-contrast font-semibold': activeEditTab === item.label }">
              <i :class="['pi', item.icon, 'mr-3 text-lg', item.color]"></i>
              <span>{{ item.label }}</span>
            </li>
          </ul>
        </div>

        <!-- Right Content -->
        <div class="flex-grow p-6 overflow-y-auto">
          <!-- General Info Tab -->
          <div v-if="activeEditTab === 'General'" class="w-full">
            <div class="flex flex-col gap-6 max-w-lg">
              <div class="field">
                <label for="edit_org_name" class="font-medium">Name</label>
                <InputText id="edit_org_name" v-model.trim="currentOrg.org_name" required="true" />
              </div>
              <div class="field">
                <label for="edit_status" class="font-medium">Status</label>
                <Dropdown id="edit_status" v-model="currentOrg.status" :options="statusOptions" optionLabel="label" optionValue="value" />
              </div>
              <div class="field">
                <label class="font-medium">Organization ID</label>
                <p class="mt-2 text-color-secondary">{{ currentOrg.org_id }}</p>
              </div>
               <div class="field">
                <label class="font-medium">Created At</label>
                <p class="mt-2 text-color-secondary">{{ new Date(currentOrg.created_at).toLocaleString() }}</p>
              </div>
            </div>
          </div>

          <!-- Users Tab -->
          <div v-if="activeEditTab === 'Users'" class="flex flex-col h-full">
            <DataTable :value="localUsers" :loading="store.usersLoading" lazy paginator
                       :totalRecords="store.pagination.total" :rows="10" @page="onPage($event)" @sort="onSort"
                       class="w-full flex-grow" responsiveLayout="scroll" scrollable scrollHeight="flex">
              <Column field="first_name" header="First Name" sortable></Column>
              <Column field="last_name" header="Last Name" sortable></Column>
              <Column field="email" header="Email" sortable></Column>
              <Column header="Role" sortable field="role.role_name">
                <template #body="slotProps">
                  <Dropdown v-if="slotProps.data.role" v-model="slotProps.data.role.role_id"
                            :options="store.roles"
                            optionLabel="role_name"
                            optionValue="role_id"
                            :disabled="!authStore.isAdmin || slotProps.data.user_id === authStore.user.user_id"
                            placeholder="Select a Role">
                  </Dropdown>
                </template>
              </Column>
              <Column field="created_at" header="Created At" sortable>
                <template #body="slotProps">
                  {{ new Date(slotProps.data.created_at).toLocaleString() }}
                </template>
              </Column>
              <Column field="last_login_at" header="Last Login" sortable>
                <template #body="slotProps">
                  {{ slotProps.data.last_login_at ? new Date(slotProps.data.last_login_at).toLocaleString() : 'Never' }}
                </template>
              </Column>
              <Column header="Active" sortable sortField="is_active">
                <template #body="slotProps">
                  <InputSwitch v-model="slotProps.data.is_active" />
                </template>
              </Column>
              <Column header="Actions" style="width: 5rem">
                <template #body="slotProps">
                  <Button v-if="authStore.isAdmin"
                          icon="pi pi-trash"
                          class="p-button-rounded p-button-warning"
                          :disabled="slotProps.data.user_id === authStore.user.user_id || slotProps.data.role.role_name === 'toolbox_super_admin'"
                          @click="confirmDeleteUser(slotProps.data)" />
                </template>
              </Column>
            </DataTable>
          </div>

          <!-- Domains Tab -->
          <div v-if="activeEditTab === 'Domains'" class="w-full">
            <div class="card">
                <DataTable :value="store.organizationDomains" :loading="store.domainsLoading" class="mb-4">
                    <Column field="domain_name" header="Domain"></Column>
                    <Column header="Users">
                      <template #body="slotProps">
                        {{ slotProps.data.user_count }}
                        <span v-if="slotProps.data.inactive_user_count > 0">
                          ({{ slotProps.data.inactive_user_count }} inactive)
                        </span>
                      </template>
                    </Column>
                    <Column header="Actions" style="width: 8rem">
                        <template #body="slotProps">
                            <Button icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="confirmDeleteDomain(slotProps.data)" />
                        </template>
                    </Column>
                </DataTable>
            </div>
            <div class="field flex items-end gap-2 mt-4">
                <div class="flex-grow">
                    <label for="new_domain">Add New Domain</label>
                    <InputText id="new_domain" v-model="newDomainName" placeholder="example.com" @keyup.enter="addNewDomain" />
                </div>
                <Button label="Add" icon="pi pi-plus" @click="addNewDomain" :loading="isAddingDomain" />
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="p-4 border-t surface-border">
            <Button label="Close" icon="pi pi-times" class="p-button-text" @click="closeEditDialog"/>
            <Button label="Save Changes" icon="pi pi-check" @click="saveOrganizationChanges" :disabled="!isDirty" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useOrganizationStore } from '@/stores/organization';
import { useAuthStore } from '@/stores/auth';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from "primevue/useconfirm";

const store = useOrganizationStore();
const authStore = useAuthStore();
const toast = useToast();
const confirm = useConfirm();

// Dialogs State
const addDialogVisible = ref(false);
const editDialogVisible = ref(false);
const newOrg = ref({ org_name: '', status: 'pending_approval' });

// Edit state
const currentOrg = ref(null);
const originalOrg = ref(null);
const localUsers = ref([]);
const originalUsers = ref([]);

const isDirty = computed(() => {
  if (!originalOrg.value) return false;
  const orgsDiffer = JSON.stringify(currentOrg.value) !== JSON.stringify(originalOrg.value);
  const usersDiffer = JSON.stringify(localUsers.value) !== JSON.stringify(originalUsers.value);
  return orgsDiffer || usersDiffer;
});

watch(() => store.organizationUsers, (newUsers) => {
  localUsers.value = JSON.parse(JSON.stringify(newUsers));
  originalUsers.value = JSON.parse(JSON.stringify(newUsers));
}, { deep: true });


// Edit Dialog Tabs State
const activeEditTab = ref('General');
const editMenu = ref([
  { label: 'General', icon: 'pi-info-circle', color: 'text-blue-500' },
  { label: 'Users', icon: 'pi-users', color: 'text-green-500' },
  { label: 'Domains', icon: 'pi-globe', color: 'text-orange-500' },
]);

const statusOptions = ref([
  { label: 'Pending Approval', value: 'pending_approval' },
  { label: 'Active', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
]);

// Users Tab State
const userSort = ref({
  sortField: 'created_at',
  sortOrder: -1
});

// Domains Tab State
const newDomainName = ref('');
const isAddingDomain = ref(false);

onMounted(() => {
  store.fetchOrganizations();
});

// --- Create Logic ---
const openCreateDialog = () => {
  newOrg.value = { org_name: '', status: 'pending_approval' };
  addDialogVisible.value = true;
};
const saveNewOrganization = async () => {
  try {
    await store.addOrganization(newOrg.value);
    addDialogVisible.value = false;
    toast.add({ severity: 'success', summary: 'Success', detail: 'Organization created', life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create organization', life: 3000 });
  }
};

// --- Edit Logic ---
const openEditDialog = (org) => {
  originalOrg.value = JSON.parse(JSON.stringify(org));
  currentOrg.value = JSON.parse(JSON.stringify(org));
  
  activeEditTab.value = 'General';
  
  store.fetchUsersForOrganization(
    org.org_id,
    1,
    10,
    userSort.value.sortField,
    userSort.value.sortOrder
  );
  store.fetchDomainsForOrganization(org.org_id);
  store.fetchRoles();
  
  editDialogVisible.value = true;
};

const closeEditDialog = () => {
  if (isDirty.value) {
    confirm.require({
      message: 'You have unsaved changes. Are you sure you want to close?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        editDialogVisible.value = false;
      },
    });
  } else {
    editDialogVisible.value = false;
  }
};

const saveOrganizationChanges = async () => {
  try {
    if (JSON.stringify(currentOrg.value) !== JSON.stringify(originalOrg.value)) {
      await store.editOrganization(currentOrg.value.org_id, currentOrg.value);
    }

    const changedUsers = localUsers.value.filter(localUser => {
      const originalUser = originalUsers.value.find(u => u.user_id === localUser.user_id);
      return JSON.stringify(localUser) !== JSON.stringify(originalUser);
    });

    if (changedUsers.length > 0) {
      await store.bulkUpdateUsers(currentOrg.value.org_id, changedUsers);
    }

    toast.add({ severity: 'success', summary: 'Success', detail: 'Changes saved successfully', life: 3000 });
    editDialogVisible.value = false;

  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save changes', life: 3000 });
  }
};

// --- Delete Logic ---
const deleteOrg = async (id) => {
  confirm.require({
      message: 'Are you sure you want to delete this organization?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      accept: async () => {
        try {
          await store.removeOrganization(id);
          toast.add({ severity: 'success', summary: 'Success', detail: 'Organization deleted', life: 3000 });
        } catch (error) {
          toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete organization', life: 3000 });
        }
      },
  });
};

// --- Users Tab Logic ---
const onPage = (event) => {
  store.fetchUsersForOrganization(
    currentOrg.value.org_id,
    event.page + 1,
    10,
    userSort.value.sortField,
    userSort.value.sortOrder
  );
};

const onSort = (event) => {
  userSort.value.sortField = event.sortField;
  userSort.value.sortOrder = event.sortOrder;
  store.fetchUsersForOrganization(
    currentOrg.value.org_id,
    1, // Reset to first page
    10,
    userSort.value.sortField,
    userSort.value.sortOrder
  );
};

const confirmDeleteUser = async (user) => {
  if (window.confirm(`Are you sure you want to delete the user "${user.first_name} ${user.last_name}"?`)) {
    try {
      await store.deleteUser(currentOrg.value.org_id, user.user_id);
      toast.add({ severity: 'success', summary: 'Success', detail: 'User deleted', life: 3000 });
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete user', life: 3000 });
    }
  }
};

// --- Domains Tab Logic ---
const addNewDomain = async () => {
    if (!newDomainName.value) return;
    isAddingDomain.value = true;
    try {
        await store.addDomainForOrganization(currentOrg.value.org_id, newDomainName.value);
        newDomainName.value = '';
        toast.add({ severity: 'success', summary: 'Success', detail: 'Domain added', life: 3000 });
    } catch (error) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to add domain', life: 3000 });
        console.error("Failed to add domain:", error);
    } finally {
        isAddingDomain.value = false;
    }
};
const confirmDeleteDomain = (domain) => {
    const activeUsers = domain.user_count - domain.inactive_user_count;
    let message = `Are you sure you want to delete the domain "${domain.domain_name}"?`;
    if (activeUsers > 0) {
        message += `\n\nWarning: This domain has ${activeUsers} active users.`;
    }

    confirm.require({
        message: message,
        header: 'Delete Confirmation',
        icon: 'pi pi-info-circle',
        acceptClass: 'p-button-danger',
        accept: async () => {
            try {
              await store.deleteDomain(currentOrg.value.org_id, domain.domain_id);
              toast.add({ severity: 'success', summary: 'Success', detail: 'Domain deleted', life: 3000 });
            } catch (error) {
              toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete domain', life: 3000 });
            }
        },
    });
};
</script>